import {
  ComponentType,
  FunctionComponent,
  createContext,
  useState,
  useContext,
} from 'react';

import { MnoProfile, User } from '@/apis/core';
import {
  restoreProfile,
  restoreUser,
  saveProfile,
  saveUser,
} from '../utils/storage';

interface AuthContextProps {
  user?: User;
  profile?: MnoProfile;
  setUser: (user: User) => void;
  setProfile: (profile: MnoProfile) => void;
}

const AuthContext = createContext<AuthContextProps>({
  /* eslint-disable @typescript-eslint/no-empty-function */
  setUser: () => {},
  setProfile: () => {},
  /* eslint-enable @typescript-eslint/no-empty-function */
});

export const AuthContextProvider: FunctionComponent = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(restoreUser());
  const [profile, setProfile] = useState<MnoProfile | undefined>(
    restoreProfile()
  );

  const handleUserChange = (user: User) => {
    setUser(user);
    saveUser(user);
  };

  const handleProfileChange = (profile: MnoProfile) => {
    setProfile(profile);
    saveProfile(profile);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        setUser: handleUserChange,
        setProfile: handleProfileChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);

export const withAuthContext =
  <P extends Record<string, never>>(WrappedComponent: ComponentType<P>) =>
  (props: P) =>
    (
      <AuthContext.Consumer>
        {(contexts) => <WrappedComponent {...props} {...contexts} />}
      </AuthContext.Consumer>
    );
